<template>
<!-- eslint-disable max-len -->
<span>
  <top-bar v-if="showDash" :next="backToChallenge" :show-player="true">
    <span slot="challenge">
      <!--
        Never use v-html with end-user generated content, because it's a vector for XSS attacks
        This is what the following line used to be:
        {{ challenge.title }}
      -->
      <div v-html="topBarText"></div>
    </span>
    <cash-navbar slot="cash"></cash-navbar>
    <metrics-navbar slot="metrics"></metrics-navbar>
  </top-bar>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <decision-prompt v-if="showDecision && !decisionMade && native" :targetElement="$refs.bottomBar" :decision="playerDecision" />
  </transition>

  <transition
    :enter-active-class="(teamDecision && (teamDecision.noAnimation || teamDecision.noAnimationEnter)) ? '' : 'animated fadeIn'"
    :leave-active-class="(teamDecision && (teamDecision.noAnimation || teamDecision.noAnimationLeave)) ? '' : 'animated fadeOut'"
  >
    <player
      v-if="showPlayer"
      :angled="teamDecision.bubbleDecision"
      :variantOverride="
        (decisionMade && teamDecision.extraPlayerVariant)
          ? teamDecision.extraPlayerVariant
          : teamDecision.playerVariant
      "
      :x-offset="teamDecision.playerXOffset ? teamDecision.playerXOffset : '0px'"
      :y-offset="teamDecision.playerYOffset ? teamDecision.playerYOffset : '0px'"
      :order-override="teamDecision.playerOrderOverride ? teamDecision.playerOrderOverride : 0"
    />
  </transition>

  <transition
    :enter-active-class="(teamDecision && (teamDecision.noAnimation || teamDecision.noAnimationEnter)) ? '' : 'animated fadeIn'"
    :leave-active-class="(teamDecision && (teamDecision.noAnimation || teamDecision.noAnimationLeave)) ? '' : 'animated fadeOut'"
  >
    <narrator
      v-if="
        showPlayer && ['choice', 'survey', 'multiselect', 'sliders', 'matching', 'matrix', 'scale'].includes(playerDecision.type)
        && teamDecision.bubbleDecision // no check for backward compat; no narrator flag or character implies using the narrator
      "
      :character="characters[teamDecision.rightCharacter]"
      :variantOverride="
        (decisionMade && teamDecision.rightExtraVariant)
          ? teamDecision.rightExtraVariant
          : teamDecision.rightVariant
      "
      :x-offset="teamDecision.rightXOffset ? teamDecision.rightXOffset : '0px'"
      :y-offset="teamDecision.rightYOffset ? teamDecision.rightYOffset : '0px'"
      :order-override="teamDecision.rightOrderOverride ? teamDecision.rightOrderOverride : 0"
    />
  </transition>

  <transition
    :enter-active-class="(teamDecision && (teamDecision.noAnimation || teamDecision.noAnimationEnter)) ? '' : 'animated fadeIn'"
    :leave-active-class="(teamDecision && (teamDecision.noAnimation || teamDecision.noAnimationLeave)) ? '' : 'animated fadeOut'"
  >
    <narrator
      v-if="
        showPlayer && ['choice', 'survey', 'multiselect', 'sliders', 'matching', 'matrix', 'scale'].includes(playerDecision.type)
        && teamDecision.bubbleDecision && (teamDecision.extraLeftCharacter || teamDecision.extraLeftNarrator)
      "
      :character="characters[teamDecision.extraLeftCharacter]"
      :variant-override="
        (decisionMade && teamDecision.extraLeftExtraVariant)
          ? teamDecision.extraLeftExtraVariant
          : teamDecision.extraLeftVariant
      "
      :x-offset="teamDecision.extraLeftXOffset ? teamDecision.extraLeftXOffset : '0px'"
      :y-offset="teamDecision.extraLeftYOffset ? teamDecision.extraLeftYOffset : '0px'"
      :order-override="teamDecision.extraLeftOrderOverride ? teamDecision.extraLeftOrderOverride : 0"
      left
    />
  </transition>

  <transition
    :enter-active-class="(teamDecision && (teamDecision.noAnimation || teamDecision.noAnimationEnter)) ? '' : 'animated fadeIn'"
    :leave-active-class="(teamDecision && (teamDecision.noAnimation || teamDecision.noAnimationLeave)) ? '' : 'animated fadeOut'"
  >
    <narrator
      v-if="
        showPlayer && ['choice', 'survey', 'multiselect', 'sliders', 'matching', 'matrix', 'scale'].includes(playerDecision.type)
        && teamDecision.bubbleDecision && (teamDecision.extraRightCharacter || teamDecision.extraRightNarrator)
      "
      :character="characters[teamDecision.extraRightCharacter]"
      :variant-override="
        (decisionMade && teamDecision.extraRightExtraVariant)
          ? teamDecision.extraRightExtraVariant
          : teamDecision.extraRightVariant
      "
      :x-offset="teamDecision.extraRightXOffset ? teamDecision.extraRightXOffset : '0px'"
      :y-offset="teamDecision.extraRightYOffset ? teamDecision.extraRightYOffset : '0px'"
      :order-override="teamDecision.extraRightOrderOverride ? teamDecision.extraRightOrderOverride : 0"
    />
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <decision-free-drag
      v-if="
        showDecision && !decisionMade
        && ['matching'].includes(playerDecision.type)
        && playerDecision.choiceDisplayType === 'freeDrag'
      "
      :decision="playerDecision"
      @input="onDecisionFreeDrag"
      key="decision-free-drag"
    ></decision-free-drag>
    <choice
      v-else-if="
        starfighter && showDecision && !decisionMade
        && ['choice', 'survey', 'multiselect', 'sliders', 'matching', 'scale', 'matrix'].includes(playerDecision.type)
        && !(teamDecision.bubbleDecision)"
      ref="choice"
      class="decision"
      :style="decisionStyle"
      :right="!(teamDecision.bubbleLeft)"
      :decision="playerDecision"
      @input="onChoice"
      :show-cost="showCost"
      key="choice"
      :shuffled-choice-order="shuffledIndices"
      :past-choices="selectedChoices"
      :submit-button-text="playerDecision.choiceSelectButtonText"
      :dynamic-height-choices="teamDecision.dynamicHeightChoices"
      :choice-height-override="teamDecision.choiceHeightOverride"
      :choice-width-override="teamDecision.choiceWidthOverride"
      :reason-back-clicked="reasonBackClicked"
    ></choice>
    <choice-bubble
      v-else-if="
        starfighter && showDecision && !decisionMade
        && ['choice', 'survey', 'multiselect', 'sliders', 'matching', 'scale', 'matrix'].includes(playerDecision.type)
        && teamDecision.bubbleDecision
      "
      ref="choiceBubble"
      :style="choiceBubbleStyle"
      :right="!(teamDecision.bubbleLeft)"
      :decision="playerDecision"
      @input="onChoice"
      :show-cost="showCost"
      key="choice-bubble"
      :shuffled-choice-order="shuffledIndices"
      :past-choices="selectedChoices"
      :submit-button-text="playerDecision.choiceSelectButtonText"
      :dynamic-height-choices="teamDecision.dynamicHeightChoices"
      :choice-height-override="teamDecision.choiceHeightOverride"
      :choice-width-override="teamDecision.choiceWidthOverride"
    ></choice-bubble>
    <allocation v-else-if="showDecision && !decisionMade && ['projectAllocation', 'roleAllocation'].includes(playerDecision.type)"
      class="decision"
      :style="decisionStyle"
      :decision="playerDecision"
      :has-submitted="hasViewedReasoningPanel"
      :selected-choices="selectedChoices"
      :colors="allocationColors"
      @input="onAllocation"
      key="allocation"
    ></allocation>
    <decision-form v-else-if="showDecision && !decisionMade && ['form'].includes(playerDecision.type)"
      class="decision"
      :style="decisionStyle"
      :decision="playerDecision"
      :selected-choices="selectedChoices"
      :submit-button-text="playerDecision.choiceSelectButtonText"
      :has-submitted="hasViewedReasoningPanel"
      @input="onDecisionForm"
      key="decision-form"
    ></decision-form>
    <reason v-else-if="starfighter && showDecision && (decisionMade || ['freeResponse'].includes(playerDecision.type)) && !(teamDecision.bubbleDecision)"
      ref="reason"
      class="decision"
      :style="reasonStyle"
      :decision="playerDecision"
      :choice="choice"
      :selectedChoices="selectedChoices"
      :choices="choices"
      :total-budget="totalBudget"
      :allocation-colors="allocationColors"
      :disable-reasoning-form="teamDecision.disableReasoningForm"
      :prevent-reasoning-input-override="teamDecision.preventReasoningInputOverride"
      :decision-reasoning-minimum-length="teamDecision.reasoningMinimumLength"
      :decision-reasoning-minimum-length-override="teamDecision.reasoningMinimumLengthOverride"
      :no-wait-for-i-frames="teamDecision.noWaitForIFrames"
      :submit-button-text="teamDecision.submitButtonText"
      :submit-button-sound="teamDecision.submitButtonSound"
      :back-button-text="teamDecision.backButtonText"
      v-model="reason"
      @back="onReasonBack"
      @input="onReasonSubmit"
      :show-cost="showCost"
      key="reason"
    ></reason>
    <reason-bubble v-else-if="showDecision && (decisionMade || ['freeResponse'].includes(playerDecision.type)) && (teamDecision.bubbleDecision || native)"
      ref="reasonBubble"
      class="reason-bubble-pos"
      :class="{ native }"
      :style="reasonBubbleStyle"
      :decision="playerDecision"
      :choice="choice"
      :selectedChoices="selectedChoices"
      :choices="choices"
      :total-budget="totalBudget"
      :allocation-colors="allocationColors"
      :disable-reasoning-form="teamDecision.disableReasoningForm"
      :prevent-reasoning-input-override="teamDecision.preventReasoningInputOverride"
      :decision-reasoning-minimum-length="teamDecision.reasoningMinimumLength"
      :decision-reasoning-minimum-length-override="teamDecision.reasoningMinimumLengthOverride"
      :no-wait-for-i-frames="teamDecision.noWaitForIFrames"
      :submit-button-text="teamDecision.submitButtonText"
      :submit-button-sound="teamDecision.submitButtonSound"
      :back-button-text="teamDecision.backButtonText"
      v-model="reason"
      @back="onReasonBack"
      @input="onReasonSubmit"
      :show-cost="showCost"
      key="reason-bubble"
      @reason-requirement-fulfilled-changed="onReasonRequirementFulfilledChanged"
    ></reason-bubble>
  </transition>

  <bottom-bar
    v-if="native"
    ref="bottomBar"
    :choiceHeight="(showDecision && !decisionMade && ['choice', 'survey', 'multiselect', 'sliders', 'matching', 'matrix', 'scale', 'roleAllocation', 'projectAllocation'].includes(playerDecision.type)) ? choicesHeight : '0'"
    :back="((showDecision && decisionMade) || enableBackToIntro) ? onClickBack : null"
    :next="(showDecision && ((decisionMadeOrFreeResponse && reasonRequirementFulfilled) || (!decisionMadeOrFreeResponse && playerDecision.type === 'multiselect'))) ? onClickApprove : null"
    :disable-next-focus="(decisionMadeOrFreeResponse && reasoningRequired)"
  >
    <template slot="choices">
      <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
        <choice-bubble
          v-if="showDecision && !decisionMade && ['choice', 'survey', 'multiselect', 'sliders', 'matching', 'matrix', 'scale'].includes(playerDecision.type)"
          :style="choiceBubbleStyle"
          :right="!(teamDecision.bubbleLeft)"
          :decision="playerDecision"
          @input="onChoice"
          :show-cost="showCost"
          key="choice-bubble"
          :shuffled-choice-order="shuffledIndices"
          :past-choices="selectedChoices"
          :submit-button-text="playerDecision.choiceSelectButtonText"
          id="choices"
          ref="choiceBubble"
        >
        </choice-bubble>
      </transition>
    </template>
    <template v-if="showDecision && (decisionMade || ['freeResponse', 'multiselect'].includes(playerDecision.type))" slot="button">
      <span v-html="submitButtonText"></span>
    </template>
  </bottom-bar>
  <bottom-bar
    v-else-if="enableBackToIntro && !decisionMade"
    ref="bottomBar"
    :back="onClickBack"
    decision-style
  >
    <span slot="back">
      <span v-html="(game && game.backButtonDefaultText) ? game.backButtonDefaultText : 'Back'"></span>
    </span>
  </bottom-bar>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <narrator-bubble
      v-if="showDecision && !decisionMade && ['choice', 'survey', 'multiselect', 'sliders', 'matching', 'matrix', 'scale'].includes(playerDecision.type) && (teamDecision.bubbleDecision || native)"
      class="narrator-bubble"
      :style="narratorBubbleStyle"
      :left="teamDecision.bubbleLeft"
      :thought="teamDecision.bubbleThought"
      :width="narratorBubbleWidth"
    >
      <div style="display: flex; flex-direction: column;" :style="narratorBubbleContentStyle">
        <!--
          Never use v-html with end-user generated content, because it's a vector for XSS attacks
        -->
        <h2 v-if="!playerDecision.hideTitle" v-html="playerDecisionDisplayTitle"></h2>
        <p style="font-size: 22px; max-height: 250px; overflow-y: auto;" v-html="playerDecisionDisplayDescription"></p>
        <div
          style="display: inline-block; max-width: 320px; max-height: 180px; margin: auto; position: relative;"
        >
          <img
            style="max-width: 320px; max-height: 180px; object-fit: contain;"
            v-show="playerDecision.image"
            :src="resolveAssetPath(playerDecision.image, 'images')"
          />
          <video
            style="display: inline-block; max-width: 320px; max-height: 180px;"
            v-show="playerDecision.video"
            :src="resolveAssetPath(playerDecision.video, 'videos')"
            controls
            playsinline
            disablePictureInPicture
            controlslist="nodownload nofullscreen noplaybackrate"
            @play="playToClick"
          >
          </video>
          <tooltip
            target="videoOrImage"
          >
          </tooltip>
        </div>
      </div>
    </narrator-bubble>
  </transition>

  <narrative-player
    v-if="showWrongChoiceNarrative"
    :narratives="wrongChoiceNarrative"
    :on-complete="onWrongChoiceNarrativesComplete"
  >
  </narrative-player>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <div
      v-if="timerInterval"
      :style="{
        position: 'absolute',
        top: (playerDecision.timerYOffset
          ? `calc(${showDash ? '100px' : '90px'} - ${playerDecision.timerYOffset})`
          : (showDash ? '100px' : '90px')),
        right: (playerDecision.timerXOffset
          ? `calc(160px - ${playerDecision.timerXOffset})`
          : '160px'),
        'z-index': '2000',
        'font-size': '220%',
        color: timeLeft < 30 ? `rgb(${7 * Math.min(30, Math.max(30 - timeLeft, 0))}, 0, 0)` : 'black',
      }"
    >
      <span style="text-shadow: 0 0 2px white, 0 0 2px white, 0 0 2px white;">{{timerString}}</span>
    </div>
  </transition>
</span>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters, mapMutations } from 'vuex';

import TopBar from '../ui/TopBar';
import BottomBar from '../ui/BottomBar';
import Player from '../ui/Player';
import CashNavbar from '../ui/CashNavbar';
import MetricsNavbar from '../ui/MetricsNavbar';
import Allocation from '../ui/Allocation';
import DecisionForm from '../ui/DecisionForm';
import DecisionFreeDrag from '../ui/DecisionFreeDrag';
import Choice from '../ui/Choice';
import Reason from '../ui/Reason';
import ChoiceBubble from '../ui/ChoiceBubble';
import ReasonBubble from '../ui/ReasonBubble';
import NarratorBubble from '../ui/NarratorBubble';
import Narrator from '../ui/Narrator';
import NarrativePlayer from '../narrative/NarrativePlayer';
import DecisionPrompt from '../ui/DecisionPrompt';
import Tooltip from '../ui/Tooltip';
import renderMarkdown from '../../../utils/markdown';

export default {
  components: {
    DecisionPrompt,
    TopBar,
    BottomBar,
    Player,
    CashNavbar,
    MetricsNavbar,
    Allocation,
    DecisionForm,
    DecisionFreeDrag,
    Choice,
    Reason,
    ChoiceBubble,
    ReasonBubble,
    NarratorBubble,
    Narrator,
    NarrativePlayer,
    Tooltip,
  },
  data() {
    return {
      showPlayer: false,
      showDecision: false,
      decisionMade: false,
      reasonBackClicked: false,
      hasViewedReasoningPanel: false,
      choice: undefined,
      shuffledIndices: [],
      selectedChoiceIndex: undefined,
      totalBudget: 0,
      choices: [],
      reason: '',
      savedReason: '',
      currentTeamDecision: {},
      submitted: false,
      errorCount: 0,
      maxErrors: 3,
      showWrongChoiceNarrative: false,
      wrongChoiceNarrative: [],
      selectedChoices: [],
      choicesElement: null,
      backBackground: null,
      timeLeft: 0,
      timerInterval: null,
      /** just used for analytics, not timer */
      timeOnDecisionReferenceDate: new Date(),
      reasonRequirementFulfilled: false
    };
  },
  computed: {
    ...mapGetters([
      'characters',
      'playerDecision',
      'playerGroupDecision',
      'playerDecisionHistory',
      'playerGroupDecisionHistory',
      'groupTeamDecision',
      'challenge',
      'playerRole',
      'dashboardState',
      'challengeIntroState',
      'teamDecisionState',
      'teamDecision',
      'tooltip',
      'game',
      'group',
      'activeClass',
      'starfighter',
      'native',
      'selectedAvatar',
      'interpolateRegex',
      'replacements',
      'activeEnvironment'
    ]),
    timerReferenceDate() {
      if (this.playerDecision.carryOverLastTimeLimit) {
        // iterate group team decisions
        for (let i = this.playerDecisionHistory.length - 1; i >= 0; --i) {
          if (!(this.playerDecisionHistory[i].carryOverLastTimeLimit)) {
            if (this.playerDecisionHistory[i].timeLimit
              && this.playerGroupDecisionHistory.length > i) {
              if (typeof this.playerGroupDecisionHistory[i].started === 'string'
                || typeof this.playerGroupDecisionHistory[i].started === 'number') {
                return new Date(this.playerGroupDecisionHistory[i].started);
              }
              return this.playerGroupDecisionHistory[i].started;
            }
            break;
          }
        }
      }
      if (typeof this.playerGroupDecision.started === 'string'
        || typeof this.playerGroupDecision.started === 'number') {
        return new Date(this.playerGroupDecision.started);
      }
      return this.playerGroupDecision.started;
    },
    timeLimit() {
      if (this.playerDecision.carryOverLastTimeLimit) {
        // console.log(this.playerDecisionHistory);
        // console.log(this.playerGroupDecisionHistory);
        // iterate group team decisions
        for (let i = this.playerDecisionHistory.length - 1; i >= 0; --i) {
          if (!(this.playerDecisionHistory[i].carryOverLastTimeLimit)) {
            if (this.playerDecisionHistory[i].timeLimit) {
              return this.playerDecisionHistory[i].timeLimit;
            }
            break;
          }
        }
      }
      return this.playerDecision.timeLimit;
    },
    enableBackToIntro() {
      const hasIntro = [
        ...((this.teamDecision || {}).introNarratives || []),
        ...((this.playerDecision || {}).introNarratives || [])
      ].length > 0;
      return hasIntro && (this.teamDecision.forceEnableBack || (this.game.enableBack && !(this.teamDecision.preventEnableBack)));
    },
    submitButtonText() {
      return (['multiselect'].includes(this.playerDecision.type))
        ? (this.playerDecision.choiceSelectButtonText
          ? this.playerDecision.choiceSelectButtonText
          : 'Select')
        : (this.teamDecision.submitButtonText
          ? this.teamDecision.submitButtonText
          : 'Reply');
    },
    choiceDisplayType() {
      if (this.playerDecision.type !== 'choice') {
        return 'list';
      }
      if (!(this.playerDecision.preventChoiceDisplayTypeOverride)
        && this.activeClass && this.activeClass.choiceDisplayTypeOverride
        && this.activeClass.choiceDisplayTypeOverride !== 'Do Not Force') {
        switch (this.activeClass.choiceDisplayTypeOverride) {
          case 'List': return 'list';
          case 'Carousel': return 'carousel';
          default: return 'list';
        }
      }
      if (!(this.playerDecision.choiceDisplayType)) return 'list';
      return this.playerDecision.choiceDisplayType;
    },
    choiceDisplayTypeWithoutOverride() {
      if (!(this.playerDecision.choiceDisplayType)) return 'list';
      return this.playerDecision.choiceDisplayType;
    },
    choiceDisplayTypeOverriddenToCarousel() {
      return this.choiceDisplayType === 'carousel'
        && this.choiceDisplayType !== this.choiceDisplayTypeWithoutOverride;
    },
    timerString() {
      if (this.timerInterval != null) {
        const minutes = Math.floor(this.timeLeft / 60);
        const seconds = this.timeLeft % 60;
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
      }
      return '';
    },
    playerDecisionDisplayTitle() {
      if (this.playerDecision) {
        return (this.playerDecision.displayTitle
          ? this.playerDecision.displayTitle : this.playerDecision.title)
          .replace(this.interpolateRegex, (__, tag) => this.replacements[tag] || '');
      }
      return '';
    },
    playerDecisionDisplayDescription() {
      if (this.playerDecision && this.playerDecision.description) {
        const displayDescription =
          this.playerDecision.description.replace(this.interpolateRegex, (__, tag) => this.replacements[tag] || '');
        if (this.playerDecision.isDescriptionMarkdown) {
          return renderMarkdown(displayDescription);
        }
        return displayDescription;
      }
      return '';
    },
    allocationColors() {
      if (this.activeEnvironment && this.activeEnvironment.allocationBarColors
      && this.activeEnvironment.allocationBarColors.length > 0) {
        const arr = JSON.parse(this.activeEnvironment.allocationBarColors);
        if (Array.isArray(arr)) {
          return arr;
        }
      }
      // TODO: Load from environment
      // ['lightsalmon', 'gold', 'mediumseagreen', 'dodgerblue'],
      return ['#FFA07A', '#FFD700', '#3CB371', '#1E90FF'];
    },
    isFreeResponse() {
      return this.playerDecision && this.playerDecision.type === 'freeResponse';
    },
    decisionMadeOrFreeResponse() {
      return this.decisionMade || this.isFreeResponse;
    },
    topBarText() {
      let t = (this.teamDecision || {}).topBarText;
      if (t == null || t === '') {
        t = this.challenge.title;
      }
      return t;
    },
    playerVariantObject() {
      if (this.teamDecision.playerVariant != null
        && this.selectedAvatar != null
        && this.selectedAvatar.imageVariants != null
        && this.selectedAvatar.imageVariants.length > 0) {
        return this.selectedAvatar.imageVariants.find(
          v => v.name === this.teamDecision.playerVariant);
      }
      return null;
    },
    rightVariantObject() {
      if (this.teamDecision.rightVariant != null
        && this.teamDecision.rightCharacter != null) {
        const rightCharacter = this.characters[this.teamDecision.rightCharacter];
        if (rightCharacter != null
          && rightCharacter.imageVariants != null
          && rightCharacter.imageVariants.length > 0) {
          return rightCharacter.imageVariants.find(
            v => v.name === this.teamDecision.rightVariant);
        }
      }
      return null;
    },
    decisionStyle() {
      // hardcoded defaults
      let topVal = (this.starfighter && !(this.showDash)) ? '45px' : '130px';
      let rightVal = '15px'; // TODO: default positioning uses right here; might wanna change that idk
      let totalYOffset = '';
      let totalXOffset = '';
      // TODO: disabling offset from image variant setting here for now; probably not desirable
      // offset from image variant setting
      // if (this.rightVariantObject != null) {
      //   if (this.rightVariantObject.speechYOffset) {
      //     totalYOffset += ` - ${this.rightVariantObject.speechYOffset}`;
      //   }
      //   if (this.rightVariantObject.speechXOffset) {
      //     // extra distance away from speaker (always on right rn)
      //     // means + instead of - because we're using right
      //     totalXOffset += ` + ${this.rightVariantObject.speechXOffset}`;
      //   }
      // }
      // offset from teamDecision offset setting
      if (this.teamDecision.bubbleYOffset) {
        totalYOffset += ` - ${this.teamDecision.bubbleYOffset}`;
      }
      if (this.teamDecision.bubbleXOffset) {
        // defined as extra distance from left
        // so - instead of + because we're using right
        totalXOffset += ` - ${this.teamDecision.bubbleXOffset}`;
      }
      // output final position style
      if (totalYOffset) {
        topVal = `calc(${topVal}${totalYOffset})`;
      }
      if (totalXOffset) {
        rightVal = `calc(${rightVal}${totalXOffset})`;
      }
      return {
        top: topVal,
        right: rightVal
      };
    },
    reasonStyle() {
      // hardcoded defaults
      let topVal = (this.starfighter && !(this.showDash)) ? '75px' : '130px';
      let rightVal = '15px'; // TODO: default positioning uses right here; might wanna change that idk
      let totalYOffset = '';
      let totalXOffset = '';
      // TODO: disabling offset from image variant setting here for now; probably not desirable
      // offset from image variant setting
      // if (this.playerVariantObject != null) {
      //   if (this.playerVariantObject.speechYOffset) {
      //     totalYOffset += ` - ${this.playerVariantObject.speechYOffset}`;
      //   }
      //   if (this.playerVariantObject.speechXOffset) {
      //     // extra distance away from player (always on left rn)
      //     // so - instead of + because we're using right
      //     totalXOffset += ` - ${this.playerVariantObject.speechXOffset}`;
      //   }
      // }
      // offset from teamDecision offset setting
      if (this.teamDecision.reasonYOffset) {
        totalYOffset += ` - ${this.teamDecision.reasonYOffset}`;
      }
      if (this.teamDecision.reasonXOffset) {
        // defined as extra distance from left
        // so - instead of + because we're using right
        totalXOffset += ` - ${this.teamDecision.reasonXOffset}`;
      }
      // output final position style
      if (totalYOffset) {
        topVal = `calc(${topVal}${totalYOffset})`;
      }
      if (totalXOffset) {
        rightVal = `calc(${rightVal}${totalXOffset})`;
      }
      return {
        top: topVal,
        right: rightVal
      };
    },
    choiceBubbleStyle() {
      let leftVal = ((this.native) ? '350px' : '312px');
      let bottomVal = '40px';
      if (!(this.teamDecision.dynamicHeightChoices) && this.choiceDisplayType === 'list' && this.playerDecision.wrongChoicesMade) {
        const count =
          Math.max(
            0,
            this.playerDecision.wrongChoicesMade
              - Math.max(
                0,
                this.playerDecision.wrongChoicesMade + this.playerDecision.choices.length
                - (this.playerDecision.choiceCountForOverflowOverride || 5)
              )
          );
        const { choiceHeightOverride } = this.teamDecision;
        const extraBottom = (this.teamDecision.displayOneLineChoices)
          ? (count * ((choiceHeightOverride || 80) + 10))
          : (Math.floor(count / 2) * (choiceHeightOverride || 160));
        bottomVal = `${40 + extraBottom}px`;
      }
      if (this.teamDecision.choiceXOffset) {
        leftVal = `calc(${leftVal} + ${this.teamDecision.choiceXOffset})`;
      }
      if (this.teamDecision.choiceYOffset) {
        bottomVal = this.choiceDisplayTypeOverriddenToCarousel
          ? `max(40px, calc(${bottomVal} + ${this.teamDecision.choiceYOffset} - 100px))`
          : `calc(${bottomVal} + ${this.teamDecision.choiceYOffset})`;
      }
      return {
        left: leftVal,
        bottom: bottomVal
      };
    },
    autoSubmit() {
      return !(this.playerDecision.forceConfirmation)
        && (this.playerDecision.forceSkipConfirmation
          || (!(this.showReasoningForm) && this.noMoreInfoToDisplay));
    },
    noMoreInfoToDisplay() {
      // no description text or carousel where description text was already displayed
      // TODO: should this logic include choice image/video?
      return !(this.anyChoiceHasDescriptionText)
        || ((!(this.playerDecision.type) || ['choice'].includes(this.playerDecision.type))
          && ['carousel'].includes(this.choiceDisplayType));
    },
    anyChoiceHasDescriptionText() {
      return this.playerDecision && this.playerDecision.choices && this.playerDecision.choices.some(c => c.description);
    },
    reasoningInputOverride() {
      if (this.isFreeResponse) return 'Force On';
      if (this.teamDecision && this.teamDecision.preventReasoningInputOverride) return 'Do Not Force';
      return (this.activeClass != null) ? this.activeClass.reasoningInputOverride :
        ((this.activeEnvironment != null && this.activeEnvironment.name != null)
          ? this.activeEnvironment.reasoningInputOverride : 'Do Not Force');
    },
    showReasoningForm() {
      if (this.reasoningInputOverride === 'Force On') {
        return true;
      } else if (this.reasoningInputOverride === 'Force Off') {
        return false;
      }
      return !(this.teamDecision.disableReasoningForm);
    },
    reasoningMinimumLength() {
      if (this.activeClass && this.activeClass.reasoningMinimumLength) {
        return this.activeClass.reasoningMinimumLength;
      }
      if (this.isFreeResponse) {
        return 4;
      }
      return 0;
    },
    reasoningRequired() {
      return (this.showReasoningForm && this.reasoningMinimumLength !== 0);
    },
    showCost() {
      return this.playerDecision != null
        && this.playerDecision.type !== 'survey'
        && this.playerDecision.type !== 'sliders'
        && this.playerDecision.type !== 'matching'
        && this.playerDecision.type !== 'matrix'
        && this.playerDecision.type !== 'scale';
    },
    showDash() {
      return ((this.showWrongChoiceNarrative)
        ? !(this.teamDecision.hideDashInChoiceNarratives)
        : !(this.teamDecision.hideDashInDecision));
    },
    choicesHeight() {
      return `${this.choicesElement ? this.choicesElement.offsetHeight : 0}px`;
    },
    narratorBubbleExtendsOffScreen() {
      return this.native /* && this.teamDecision.bubbleDecision */
        && (this.teamDecision.rightCharacter || this.teamDecision.extraRightCharacter);
    },
    narratorBubbleWidth() {
      if (this.starfighter) {
        return '900px';
      }
      if (this.narratorBubbleExtendsOffScreen) {
        return '1400px';
      }
      return '1050px';
    },
    narratorBubbleStyle() {
      // hardcoded defaults based on theme
      let topVal =
        ((this.showDash) ? '123px' : ((this.native) ? '65px' : '135px'));
      let leftVal =
        (!(this.native) ? '312px' : ((this.teamDecision.bubbleDecision) ? '390px' : '270px'));
      let totalYOffset = '';
      let totalXOffset = '';
      // TODO: disabling offset from image variant setting here for now; might be undesirable rn;
      //       want to match NarrativeDecisionBubble behavior, which currently is not using it.
      // offset from image variant setting
      // if (this.rightVariantObject != null) {
      //   if (this.rightVariantObject.speechYOffset) {
      //     totalYOffset += ` - ${this.rightVariantObject.speechYOffset}`;
      //   }
      //   if (this.rightVariantObject.speechXOffset) {
      //     // extra distance away from speaker (always on right rn)
      //     totalXOffset += ` - ${this.rightVariantObject.speechXOffset}`;
      //   }
      // }
      // offset from teamDecision offset setting
      if (this.teamDecision.bubbleYOffset) {
        totalYOffset += ` - ${this.teamDecision.bubbleYOffset}`;
      }
      if (this.teamDecision.bubbleXOffset) {
        // defined as extra distance from left
        totalXOffset += ` + ${this.teamDecision.bubbleXOffset}`;
      }
      // output final position style
      if (totalYOffset) {
        topVal = `calc(${topVal}${totalYOffset})`;
      }
      if (totalXOffset) {
        leftVal = `calc(${leftVal}${totalXOffset})`;
      }
      return {
        top: topVal,
        left: leftVal
      };
    },
    narratorBubbleContentStyle() {
      if (this.native) {
        return {
          margin: (this.narratorBubbleExtendsOffScreen ? '-54px 454px -42px -16px' : '-54px -24px -42px -16px'),
          'max-height': `${this.showDash ? '200px' : '324px'}`,
          'line-height': '1.5'
        };
      }
      return '';
    },
    reasonBubbleStyle() {
      // hardcoded defaults
      let topVal = '135px';
      let leftVal = '312px';
      let totalYOffset = '';
      let totalXOffset = '';
      // TODO: disabling offset from image variant setting here for now; probably not desirable
      // offset from image variant setting
      // if (this.playerVariantObject != null) {
      //   if (this.playerVariantObject.speechYOffset) {
      //     totalYOffset += ` - ${this.playerVariantObject.speechYOffset}`;
      //   }
      //   if (this.playerVariantObject.speechXOffset) {
      //     // extra distance away from player (always on left rn)
      //     totalXOffset += ` + ${this.playerVariantObject.speechXOffset}`;
      //   }
      // }
      // offset from teamDecision offset setting
      if (this.teamDecision.reasonYOffset) {
        totalYOffset += ` - ${this.teamDecision.reasonYOffset}`;
      }
      if (this.teamDecision.reasonXOffset) {
        // defined as extra distance from left
        totalXOffset += ` + ${this.teamDecision.reasonXOffset}`;
      }
      // output final position style
      if (totalYOffset) {
        topVal = `calc(${topVal}${totalYOffset})`;
      }
      if (totalXOffset) {
        leftVal = `calc(${leftVal}${totalXOffset})`;
      }
      return {
        top: topVal,
        left: leftVal
      };
    },
  },
  methods: {
    ...mapActions([
      'playVoice',
      'stopSounds',
      'flashDanger',
      'setTooltips',
      'advanceTooltip',
      'rewindToTooltipTarget',
      'setLastStateBeforeChallengeIntro',
      'setTeamDecisionStarted',
      'submitAllocation',
      'submitChoice',
      'submitDecisionForm',
      'submitMultiselect',
      'submitMatching',
      'submitMatrix',
      'submitScale',
      'submitSliders'
    ]),
    ...mapMutations([
      'setRootLoadingMessage',
      'setRootLoadingMessageResetTimer',
      'setStartAtLastNarrative',
      'setBackToDashboard',
    ]),
    playToClick() {
      window.dispatchEvent(new Event('click'));
    },
    shuffle(array) {
      // ***
      // Fisher-Yates JS implementation from: https://stackoverflow.com/a/2450976
      // ***
      let currentIndex = array.length;
      let temporaryValue;
      let randomIndex;
      // While there remain elements to shuffle...
      while (currentIndex !== 0) {
        // Pick a remaining element...
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex -= 1;
        // And swap it with the current element.
        temporaryValue = array[currentIndex];
        array[currentIndex] = array[randomIndex];
        array[randomIndex] = temporaryValue;
      }
      return array;
    },
    onReasonRequirementFulfilledChanged(newValue) {
      this.reasonRequirementFulfilled = newValue;
    },
    onChoice(data) {
      if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
        return;
      }
      if (this.playerDecision.type === 'sliders') {
        this.reason = this.savedReason;
        this.selectedChoices = data;
      } else if (this.playerDecision.type === 'matching') {
        this.reason = this.savedReason;
        this.selectedChoices = data;
      } else if (this.playerDecision.type === 'scale') {
        this.reason = this.savedReason;
        this.selectedChoices = data;
      } else if (this.playerDecision.type === 'matrix') {
        this.reason = this.savedReason;
        this.selectedChoices = data;
      } else if (this.playerDecision.type === 'multiselect') {
        if (this.selectedChoices.length !== data.length
          || this.selectedChoices.some(sc => !(data.includes(sc)))
          || data.some(d => !(this.selectedChoices.includes(d)))) {
          this.reason = '';
          this.savedReason = '';
        } else {
          this.reason = this.savedReason;
        }
        this.selectedChoices = data;
      } else {
        const { choice, selectedChoiceIndex } = data;
        this.choice = choice;
        if (selectedChoiceIndex === this.selectedChoiceIndex) {
          this.reason = this.savedReason;
        } else {
          this.reason = '';
          this.savedReason = '';
        }
        this.selectedChoiceIndex = selectedChoiceIndex;

        if (choice.playerBackgroundChange) {
          this.backBackground = this.playerRole.background;
          this.playerRole.background = choice.playerBackgroundChange;
        }
      }
      this.reasonRequirementFulfilled =
        !(this.reasoningRequired && this.reason.length < this.reasoningMinimumLength);
      this.decisionMade = true;
    },
    onDecisionFreeDrag({ choices }) {
      this.choices = choices;
      this.selectedChoices = choices;
      this.decisionMade = true;
    },
    onAllocation({ choices, totalBudget }) {
      if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
        return;
      }
      this.hasViewedReasoningPanel = true;
      this.choices = choices;
      this.selectedChoices = choices;
      this.totalBudget = totalBudget;
      this.decisionMade = true;
    },
    onDecisionForm({ choices }) {
      if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
        return;
      }
      this.hasViewedReasoningPanel = true;
      this.choices = choices;
      this.selectedChoices = choices;
      this.decisionMade = true;
    },
    async onReasonBack(reason) {
      if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
        return;
      }
      this.savedReason = reason;
      if (this.backBackground) {
        this.playerRole.background = this.backBackground;
        this.backBackground = null;
      }
      this.reasonBackClicked = true;
      this.stopSounds(); // stops any choice description voice line (or any sfx played by Reason if that is added)
      this.decisionMade = false;
      await new Promise(resolve => setTimeout(resolve, 475)); // wait for tooltip exit delay
      await this.rewindToTooltipTarget({ target: 'reasonAndConfirmChoiceBubble', tooltips: this.teamDecision.tooltips || [] });
      // console.log(this.tooltip);
    },
    async onWrongChoiceNarrativesComplete() {
      this.showWrongChoiceNarrative = false;
      const secondsSpentOnDecision = (Date.now() - this.timeOnDecisionReferenceDate.valueOf()) / 1000;
      const { choice, reason } = this;
      await this.submitChoice({
        decision: this.playerDecision,
        choice,
        reason,
        duration: secondsSpentOnDecision,
      });
      await Vue.nextTick();
      this.$router.push(this.dashboardState);
    },
    async onReasonSubmit(reason) {
      if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
        return;
      }
      if (this.submitted) return;
      this.submitted = true;
      const secondsSpentOnDecision = (Date.now() - this.timeOnDecisionReferenceDate.valueOf()) / 1000;

      try {
        switch (this.playerDecision.type) {
          case 'survey':
          case 'choice':
            {
              const { choice } = this;
              if (choice.wrong && choice.narratives && choice.narratives.length > 0) {
                this.wrongChoiceNarrative = choice.narratives;
                this.showWrongChoiceNarrative = true;
                this.showDecision = false;
                this.showPlayer = false;
              } else {
                await this.submitChoice({
                  decision: this.playerDecision,
                  choice,
                  reason,
                  duration: secondsSpentOnDecision,
                });
                await Vue.nextTick();
                this.$router.push(this.dashboardState);
              }
            }
            break;
          case 'freeResponse':
            // eslint-disable-next-line no-lone-blocks
            {
              await this.submitChoice({
                decision: this.playerDecision,
                choice: {
                  title: 'Free Response'
                },
                reason,
                duration: secondsSpentOnDecision,
              });
              await Vue.nextTick();
              this.$router.push(this.dashboardState);
            }
            break;
          case 'sliders':
            {
              const { selectedChoices } = this;
              await this.submitSliders({
                decision: this.playerDecision,
                selectedChoices,
                reason,
                duration: secondsSpentOnDecision,
              });
              await Vue.nextTick();
              this.$router.push(this.dashboardState);
            }
            break;
          case 'matching':
            {
              const { selectedChoices } = this;
              await this.submitMatching({
                decision: this.playerDecision,
                selectedChoices,
                reason,
                duration: secondsSpentOnDecision,
              });
              await Vue.nextTick();
              this.$router.push(this.dashboardState);
            }
            break;
          case 'scale':
            {
              const { selectedChoices } = this;
              await this.submitScale({
                decision: this.playerDecision,
                selectedChoices,
                reason,
                duration: secondsSpentOnDecision,
              });
              await Vue.nextTick();
              this.$router.push(this.dashboardState);
            }
            break;
          case 'matrix':
            {
              const { selectedChoices } = this;
              await this.submitMatrix({
                decision: this.playerDecision,
                selectedChoices,
                reason,
                duration: secondsSpentOnDecision,
              });
              await Vue.nextTick();
              this.$router.push(this.dashboardState);
            }
            break;
          case 'multiselect':
            {
              const { selectedChoices } = this;
              await this.submitMultiselect({
                decision: this.playerDecision,
                selectedChoices,
                reason,
                duration: secondsSpentOnDecision,
              });
              await Vue.nextTick();
              this.$router.push(this.dashboardState);
            }
            break;
          case 'projectAllocation':
          case 'roleAllocation':
            {
              const { choices, totalBudget } = this;
              await this.submitAllocation({
                decision: this.playerDecision,
                choices,
                totalBudget,
                reason,
                duration: secondsSpentOnDecision,
              });
              await Vue.nextTick();
              this.$router.push(this.dashboardState);
            }
            break;
          case 'form':
            {
              const { choices } = this;
              await this.submitDecisionForm({
                decision: this.playerDecision,
                choices,
                reason,
                duration: secondsSpentOnDecision,
              });
              await Vue.nextTick();
              this.$router.push(this.dashboardState);
            }
            break;
          default:
            await Vue.nextTick();
            this.$router.push(this.dashboardState);
            break;
        }
      } catch (err) {
        console.log(err);
        if (await this.$router.handleGameError(err)) return;
        this.errorCount += 1;
        if (this.errorCount >= this.maxErrors) return this.$router.showGameErrorCritical();
        this.submitted = false;
        if (this.autoSubmit) {
          this.onReasonBack(reason);
          this.flashDanger({ message: 'Network error. Please try again.', duration: 2500 });
        }
      }
    },
    backToChallenge() { // deprecated?
      const lastStateBeforeChallengeIntro = this.teamDecisionState;
      this.setLastStateBeforeChallengeIntro({ lastStateBeforeChallengeIntro });
      this.$router.push(this.challengeIntroState);
    },
    getReasonRef() {
      if (this.$refs && this.$refs.reason) {
        return this.$refs.reason;
      }
      if (this.$refs && this.$refs.reasonBubble) {
        return this.$refs.reasonBubble;
      }
      return null;
    },
    async getReasonRefAsync() {
      while (this.getReasonRef() == null) {
        await new Promise(resolve => setTimeout(resolve, 250));
      }
      return this.getReasonRef();
    },
    getChoiceRef() {
      if (this.$refs && this.$refs.choice) {
        return this.$refs.choice;
      }
      if (this.$refs && this.$refs.choiceBubble) {
        return this.$refs.choiceBubble;
      }
      return null;
    },
    async getChoiceRefAsync() {
      while (this.getChoiceRef() == null) {
        await new Promise(resolve => setTimeout(resolve, 250));
      }
      return this.getChoiceRef();
    },
    async onClickBack() {
      if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
        return;
      }
      if (this.decisionMade) {
        await ((await this.getReasonRefAsync()).onClickBack());
      } else {
        this.setStartAtLastNarrative();
        this.setBackToDashboard();
        this.$router.push(this.dashboardState);
      }
    },
    async onClickApprove() {
      if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
        return;
      }
      if (this.decisionMade) {
        await ((await this.getReasonRefAsync()).onClickApprove());
      } else if (this.native && this.playerDecision.type === 'multiselect') {
        await ((await this.getChoiceRefAsync()).onClickChoice());
      }
    }
  },
  created() {
    this.currentTeamDecision = this.groupTeamDecision;
    this.playerDecision.displayOneLineChoices = this.teamDecision.displayOneLineChoices;
  },
  async mounted() {
    this.timeOnDecisionReferenceDate = new Date();
    if (this.playerDecision && this.playerDecision.type
      && ['choice', 'survey'].includes(this.playerDecision.type)
      && this.timeLimit
      && this.timerReferenceDate) {
      this.timeLeft =
        Math.max(
          0,
          this.timeLimit
            - Math.round((Date.now() - this.timerReferenceDate.valueOf()) / 1000)
        );
      if (this.timeLeft < 0.001) {
        // time has run out, find and submit choice
        this.setRootLoadingMessage(this.playerDecision.timerEndMessage || 'Ran Out Of Time');
        this.setRootLoadingMessageResetTimer(this.playerDecision.timerEndMessageDuration || 2000);
        let index = this.playerDecision.choices.findIndex(c => c.default);
        if (index < 0) {
          index = this.playerDecision.choices.length - 1;
        }
        await this.setTeamDecisionStarted();
        this.onChoice({ choice: this.playerDecision.choices[index], selectedChoiceIndex: index });
        this.onReasonSubmit('Ran Out Of Time');
        return;
      }
    }
    this.showPlayer = true;
    if (this.playerDecision && this.playerDecision.choices) {
      this.shuffledIndices = this.shuffle(this.playerDecision.choices.map((_, index) => index));
    }
    await new Promise(resolve => setTimeout(resolve, 1000));
    if (this.timeLimit && !this.timerReferenceDate) {
      while (!this.playerGroupDecision.started) {
        try {
          await this.setTeamDecisionStarted(); // important that this gets set for the timer logic (and analytics at some point)
        } catch (err) {
          this.errorCount += 1;
          if (this.errorCount >= this.maxErrors) return this.$router.showGameErrorCritical();
        }
      }
    }
    // TODO: this was causing problems in cases where you have packet loss and/or high latency to the backend
    // TODO: we can do time-on-decision analytics better with a dedicated solution for that anyway
    // else if (this.timeLimit && this.timerReferenceDate && !this.playerGroupDecision.started) {
    //   // timer already has a reference date, but ideally we do want the started variable set for analytics
    //   const trySetDecisionStarted = async() => {
    //     if (!this.playerGroupDecision.started) {
    //       try {
    //         await this.setTeamDecisionStarted();
    //       } catch (err) {
    //         this.errorCount += 1;
    //         if (this.errorCount >= this.maxErrors) return this.$router.showGameErrorCritical();
    //       }
    //     }
    //     // if it's still not set, try again
    //     if (!this.playerGroupDecision.started) setTimeout(trySetDecisionStarted, 1);
    //   };
    //   setTimeout(trySetDecisionStarted, 1);
    // }
    this.showDecision = true;
    await Vue.nextTick();
    if ((this.teamDecision.bubbleDecision || this.native) && !(this.reasonBackClicked)) {
      let shouldQueue = false;
      if (!(this.playerDecision.hideTitle) && this.playerDecision.titleVoiceLine) {
        shouldQueue = true;
        if (this.playerDecision.descriptionVoiceLine) {
          this.playVoice({ line: this.playerDecision.titleVoiceLine, next: this.playerDecision.descriptionVoiceLine });
        } else {
          this.playVoice({ line: this.playerDecision.titleVoiceLine });
        }
      } else if (this.playerDecision.descriptionVoiceLine) {
        shouldQueue = true;
        this.playVoice({ line: this.playerDecision.descriptionVoiceLine });
      }

      if (this.playerDecision.promptVoiceLine) {
        await this.$nextTick();
        this.playVoice({ line: this.playerDecision.promptVoiceLine, queue: shouldQueue, });
      }
    }
    this.choicesElement = document.getElementById('choices');
    // console.log(this.playerDecision);
    // console.log(this.timeLimit);
    // if time limit, start timer
    if (this.playerDecision && this.playerDecision.type
      && ['choice', 'survey'].includes(this.playerDecision.type)
      && this.timeLimit
      && this.timerReferenceDate) {
      this.timeLeft =
        Math.max(
          0,
          this.timeLimit
            - Math.round((Date.now() - this.timerReferenceDate.valueOf()) / 1000)
        );
      this.timerInterval = setInterval(() => {
        if (this.tooltip
          && (this.tooltip.preventStateProgressionWhileOpen
            || this.tooltip.preventOtherInputWhileOpen)) {
          return;
        }
        this.timeLeft =
          Math.max(
            0,
            this.timeLimit
              - Math.round((Date.now() - this.timerReferenceDate.valueOf()) / 1000)
          );
        if (this.timeLeft < 0.001) {
          // time has run out, find and submit choice
          if (this.playerDecision.timerEndMessageDuration) {
            this.setRootLoadingMessage(this.playerDecision.timerEndMessage || 'Ran Out Of Time');
            this.setRootLoadingMessageResetTimer(this.playerDecision.timerEndMessageDuration);
          }
          let index = this.playerDecision.choices.findIndex(c => c.default);
          if (index < 0) {
            index = this.playerDecision.choices.length - 1;
          }
          this.onChoice({ choice: this.playerDecision.choices[index], selectedChoiceIndex: index });
          this.onReasonSubmit('Ran Out Of Time');
          clearInterval(this.timerInterval);
          this.timerInterval = null;
        }
      }, 500);
    }
    if (this.teamDecision) {
      this.setTooltips({ tooltips: this.teamDecision.tooltips || [] });
      await new Promise(resolve => setTimeout(resolve, 50));
      this.advanceTooltip(); // show first tooltip
    }
  },
  destroyed() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }
};
</script>

<style lang="scss" scoped>
.decision {
  top: 130px;
  right: 15px;
}
.narrator-bubble {
  left: 312px;
  top: 135px;
  max-height: 330px;
  font-weight: normal;
}
.reason-bubble-pos {
  top: 135px;
  left: 312px;
  z-index: 10;
}
.reason-bubble-pos.native {
  z-index: -100;
}

.td-prompt {
  position: absolute;
  top: 412px;
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  height: 60px;
  background-color: var(--primaryColor);
  color: white;
  text-align: center;
  z-index: -1;

  border: 2px solid white;
  font-size: 25px;
}
</style>



// WEBPACK FOOTER //
// src/components/game/pages/TeamDecision.vue